import { useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import { AuthShell } from "../components/AuthShell";
import { PhoneShowcase } from "../components/PhoneShowcase";
import { Button } from "../components/ui/Button";
import { DURATION, EASE_OUT_SOFT } from "../lib/motion";
import { hydrateSession } from "../lib/authSession";

/**
 * Beyond Sure — OTP Verification Screen (v3.4).
 *
 * Uses the SAME AuthShell as the sign-in screen so the desktop split, phone
 * stack, fonts, glass, background, and showcase are pixel-identical. Only
 * the middle column content differs.
 *
 * Slots:
 *   • 6 separate digit inputs — auto-advance on type, backspace to previous,
 *     full-code paste support
 *   • Resend code with a countdown timer (30s lockout)
 *   • Black Verify button — disabled until all 6 digits entered, loading
 *     state during verify
 *   • "← Use a different email" back link
 *
 * Wired flow (commit 8cf11ef):
 *   • onSubmit  → POST /api/otp/verify {email,code,agreed_to_terms}
 *                 then hydrate localStorage (bsure_token + ig_t/ig_u +
 *                 bsure_email) byte-symmetric with LoginRoute's Google-
 *                 OAuth return handler. onVerified fires with {isNew}.
 *   • onResend  → POST /api/otp/send to re-deliver. Cooldown restarts
 *                 whether the request succeeded or not (server-side
 *                 rate-limit prevents abuse regardless of UI state).
 */

const CODE_LENGTH = 6;
const RESEND_COOLDOWN_S = 30;

interface OtpScreenProps {
  email: string;
  /** Fires on successful /api/otp/verify. `isNew` distinguishes a brand-new
   * account (route to onboarding) from a returning sign-in (route to /chat
   * directly), mirroring the Google-OAuth return handling in LoginRoute. */
  onVerified: (result: { isNew: boolean }) => void;
  onBack: () => void;
  /** BD1 signup mode: when set, the code submits to /api/auth/signup with
   * this password (chosen on CreatePasswordScreen) instead of
   * /api/otp/verify — one call creates the account, sets the bcrypt hash,
   * and mints the session. Absent = classic OTP verify (unchanged). */
  signupPassword?: string;
}

function ArrowRightGlyph() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path
        d="M3 8h10M9 4l4 4-4 4"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function OtpScreen({ email, onVerified, onBack, signupPassword }: OtpScreenProps) {
  // The code is stored as an array of single characters so each cell can
  // own one slot. Mutating via setCode([...next]) keeps React's diffing happy.
  const [code, setCode] = useState<string[]>(() => Array(CODE_LENGTH).fill(""));
  const [loading, setLoading] = useState(false);
  const [errored, setErrored] = useState(false);
  // BD1: server-provided failure detail (e.g. signup 409 "account already
  // exists"). Shown instead of the generic wrong-code line when present.
  const [serverMsg, setServerMsg] = useState<string | null>(null);
  const [cooldown, setCooldown] = useState(RESEND_COOLDOWN_S);
  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);
  // AY3 (AV G2 parity): one-shot guard so a complete code verifies exactly once —
  // the type-path and paste-path auto-submits (and a rapid double-tap) can't fire
  // two /api/otp/verify calls. Reset on a recoverable failure so the user can retry.
  const submittingRef = useRef(false);

  // Resend cooldown timer — starts on mount, restarts when the user
  // explicitly resends. Tracks remaining seconds for the visible label.
  useEffect(() => {
    if (cooldown <= 0) return;
    const id = window.setTimeout(() => setCooldown((s) => s - 1), 1000);
    return () => window.clearTimeout(id);
  }, [cooldown]);

  // Focus the first input on mount so the user can just start typing.
  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  const filled = useMemo(() => code.every((c) => /^\d$/.test(c)), [code]);

  function setDigit(idx: number, value: string) {
    // Only accept a single digit. If the user pasted multiple chars into
    // one cell we route through handlePaste — that runs in a different
    // handler. This guard keeps the per-cell handler simple.
    if (!/^\d?$/.test(value)) return;
    const next = [...code];
    next[idx] = value;
    setCode(next);
    setErrored(false);
    if (value && idx < CODE_LENGTH - 1) {
      inputRefs.current[idx + 1]?.focus();
    }
    // AY3 (AV G1 parity): typing the final digit auto-submits, matching the
    // legacy form.html. runVerify is hoisted; pass the fresh code string.
    if (next.every((c) => /^\d$/.test(c))) void runVerify(next.join(""));
  }

  function handleKeyDown(idx: number, e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Backspace") {
      if (code[idx]) {
        // Default behaviour will clear this cell; nothing more to do.
        return;
      }
      // Already empty — jump back and clear the previous cell.
      if (idx > 0) {
        e.preventDefault();
        const next = [...code];
        next[idx - 1] = "";
        setCode(next);
        inputRefs.current[idx - 1]?.focus();
      }
    } else if (e.key === "ArrowLeft" && idx > 0) {
      e.preventDefault();
      inputRefs.current[idx - 1]?.focus();
    } else if (e.key === "ArrowRight" && idx < CODE_LENGTH - 1) {
      e.preventDefault();
      inputRefs.current[idx + 1]?.focus();
    } else if (e.key === "Enter" && filled) {
      e.preventDefault();
      handleSubmit();
    }
  }

  function handlePaste(idx: number, e: React.ClipboardEvent<HTMLInputElement>) {
    const text = e.clipboardData.getData("text").trim();
    const digits = text.replace(/\D/g, "");
    if (!digits) return;
    e.preventDefault();
    const next = [...code];
    for (let i = 0; i < CODE_LENGTH; i++) {
      next[i] = digits[i] ?? "";
    }
    setCode(next);
    setErrored(false);
    // Focus the next empty cell or the last cell if all filled.
    const lastFilled = Math.min(idx + digits.length, CODE_LENGTH) - 1;
    inputRefs.current[Math.min(lastFilled + 1, CODE_LENGTH - 1)]?.focus();
    // AY3 (AV G1 parity): a full pasted code auto-submits, matching the type path.
    if (next.every((c) => /^\d$/.test(c))) void runVerify(next.join(""));
  }

  // AY3: the verify call, taking the code EXPLICITLY so the auto-submit paths
  // (setDigit / handlePaste) pass the fresh code without waiting for state to
  // flush. submittingRef is the AV G2 double-submit guard — stronger than the old
  // `loading` check, which a same-tick auto-submit could race past.
  async function runVerify(fullCode: string) {
    if (fullCode.length !== CODE_LENGTH || submittingRef.current) return;
    submittingRef.current = true;
    setLoading(true);
    setErrored(false);
    setServerMsg(null);
    // POST /api/otp/verify — or /api/auth/signup in BD1 signup mode (same
    // emailed code, plus the chosen password; the server creates the account
    // AND sets the bcrypt hash in one step). agreed_to_terms is sent
    // unconditionally because SignInScreen already gates on the T&C checkbox.
    try {
      const endpoint = signupPassword ? "/api/auth/signup" : "/api/otp/verify";
      const body = signupPassword
        ? { email, code: fullCode, password: signupPassword, agreed_to_terms: true }
        : { email, code: fullCode, agreed_to_terms: true };
      const r = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok || !data?.token || !data?.user) {
        setErrored(true);
        // Surface a specific server reason when it isn't the plain wrong-code
        // case (signup 409 account_exists, policy 400, rate-limit 429).
        if (data && (data.message || data.error) && r.status !== 401) {
          setServerMsg(data.message || data.error);
        }
        setLoading(false);
        submittingRef.current = false; // recoverable — allow a corrected retry
        return;
      }
      // Hydrate both storage conventions so React (bsure_token) and the legacy
      // form.html (ig_t / ig_u) both see the session. Mirrors LoginRoute's
      // Google-OAuth return handler — keeping the paths byte-symmetric.
      hydrateSession(data);
      setLoading(false);
      onVerified({ isNew: Boolean(data.user.is_new) });
    } catch {
      setErrored(true);
      setLoading(false);
      submittingRef.current = false; // network blip — allow a retry
    }
  }

  // Manual submit (Verify button, Enter key, form submit) → delegates to runVerify.
  function handleSubmit(e?: React.FormEvent) {
    if (e) e.preventDefault();
    void runVerify(code.join(""));
  }

  async function handleResend() {
    if (cooldown > 0) return;
    // Re-trigger /api/otp/send. We restart the cooldown unconditionally so
    // the user gets visual feedback even if the network blip swallowed the
    // request — the resend rate-limit lives server-side anyway, so spamming
    // this button can't actually email-bomb anyone.
    try {
      await fetch("/api/otp/send", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
    } catch {
      /* network blip — cooldown still resets so the UI gives feedback */
    }
    setCooldown(RESEND_COOLDOWN_S);
  }

  return (
    <AuthShell
      eyebrow="Sign in · Free 7-day trial"
      headline="Insure smarter, live better."
      subhead="BeyondSure is India's most intelligent AI insurance advisor — in English, Hindi, and 10+ Indian languages."
      formTitle="Enter your code."
      showcase={<PhoneShowcase />}
    >
      <motion.form
        onSubmit={handleSubmit}
        noValidate
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: DURATION.base, ease: EASE_OUT_SOFT }}
        className="bsure-ui flex flex-col gap-5"
      >
        <p className="bsure-prose text-[13.5px] leading-[1.55] text-[var(--color-ink-60)]">
          We sent a 6-digit code to{" "}
          <span className="text-[var(--color-ink)] font-medium">{email}</span>.
          It arrives within a minute — check your spam folder if it doesn't.
        </p>

        {/* 6 digit cells */}
        <div
          className="flex items-center gap-2 sm:gap-2.5"
          role="group"
          aria-label="One-time passcode"
        >
          {code.map((digit, i) => (
            <input
              key={i}
              ref={(el) => {
                inputRefs.current[i] = el;
              }}
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={1}
              pattern="[0-9]*"
              aria-label={`Digit ${i + 1} of ${CODE_LENGTH}`}
              value={digit}
              onChange={(e) => setDigit(i, e.target.value)}
              onKeyDown={(e) => handleKeyDown(i, e)}
              onPaste={(e) => handlePaste(i, e)}
              className={[
                "h-12 w-10 sm:h-14 sm:w-12 text-center",
                "bsure-display text-[22px] sm:text-[26px] text-[var(--color-ink)]",
                "bg-[var(--color-paper)]",
                "rounded-[var(--radius-md)]",
                "border-[1.5px] outline-none",
                "transition-[border-color,box-shadow] duration-[var(--duration-base)]",
                errored
                  ? "border-[var(--color-ink)]"
                  : "border-[var(--color-ink-16)] focus:border-[var(--color-brand)] focus:shadow-[0_0_0_4px_rgb(62_142_91_/_0.12)]",
              ].join(" ")}
              style={{ fontWeight: 700 }}
            />
          ))}
        </div>

        {errored ? (
          <p className="text-[12.5px] text-[var(--color-ink)] font-medium">
            {serverMsg || "That code didn't match. Try again or resend a new one."}
          </p>
        ) : null}

        {/* Resend */}
        <div className="flex items-center justify-between text-[12.5px]">
          <span className="text-[var(--color-ink-60)]">
            Didn't get it?{" "}
            {cooldown > 0 ? (
              <span className="text-[var(--color-ink-40)]">
                Resend in {cooldown}s
              </span>
            ) : (
              <button
                type="button"
                onClick={handleResend}
                className="text-[var(--color-brand-deep)] underline underline-offset-2 hover:text-[var(--color-ink)] transition-colors font-medium"
              >
                Resend code
              </button>
            )}
          </span>
        </div>

        <Button
          type="submit"
          variant="primary"
          fullWidth
          loading={loading}
          disabled={!filled}
          trailing={<ArrowRightGlyph />}
        >
          Verify
        </Button>

        <button
          type="button"
          onClick={onBack}
          className="bsure-ui mt-1 text-[12.5px] text-[var(--color-ink-60)] hover:text-[var(--color-ink)] transition-colors self-start"
        >
          ← Use a different email
        </button>
      </motion.form>
    </AuthShell>
  );
}

export default OtpScreen;
